// Объём упаковки утеплителя — основа цены за м³ в «умном подборе утеплителя».
//
// Готовому полю «Объем изделий в м3» доверять нельзя: в 68 строках из 1838 оно
// противоречит названию, причём права оказывается то одна сторона, то другая.
// Но и названию целиком доверять нельзя тоже — в нём врут все три числа, просто
// поодиночке и в разных позициях:
//
//   «Роквул FIRE BATTS 25 мм 600х1000 (0,2 м3; 3,6 м2; 6 шт)» — объём 0,2 списан
//       с 50-миллиметровой позиции, хотя 0,025 × 3,6 = 0,09;
//   «Изомин ThermoWool Оптима 50 мм 600х1200 (0,288 м3; 5,76 м2; 6 шт)» — плит не 6,
//       а 8: 5,76 м² ÷ (0,6 × 1,2). Объём 0,288 при этом верный;
//   «Кнауф Терм Теплая стена 150 х 600 х 1200 мм (0,432 м3; 2 м2; 4 шт)» — врёт
//       площадь, а объём и количество плит сходятся: 0,15 × 0,6 × 1,2 × 4 = 0,432.
//
// Поэтому объём не «берётся» откуда-то, а голосуется. Есть четыре независимых
// способа его получить, и в каждом сомнительном случае ровно два из них сходятся
// между собой — они и дают ответ, третий остаётся в меньшинстве:
//
//   1. толщина × площадь из скобок;
//   2. толщина × ширина × длина × количество плит;
//   3. объём, записанный в скобках названия;
//   4. поле справочника «Объем изделий в м3».
//
// Расхождение не абстрактное: цена за м³ — единственная величина, по которой
// подбор выбирает «самое дешёвое», и ошибка в объёме сразу двигает товар в топ
// или выбрасывает из него.
//
// Разбор габаритов по образцу src/utils/osbDims.js (KAL-43) — там та же задача
// решена для ОСБ.

// Разделитель габаритов: кириллическая «х», латинская «x», «×» или «*».
const X = "[хХxX×*]";
const NUM = "(\\d+(?:[.,]\\d+)?)";

// Три числа подряд через разделитель: «Пеноплэкс Гео С 80 х 585 х 1185 мм»,
// «ПСБ С - 15 1200 х 1000 х 100 мм». Порядок у поставщиков разный — какое из
// трёх чисел толщина, решает не позиция, а размер (см. pickThickness).
const DIMS_TRIPLE = new RegExp(`${NUM}\\s*${X}\\s*${NUM}\\s*${X}\\s*${NUM}\\s*мм`);

// Толщина отдельно, потом лист: «Роквул Лайт Баттс Скандик 150 мм 600х1200».
const DIMS_T_THEN_WL = new RegExp(`${NUM}\\s*мм\\s+${NUM}\\s*${X}\\s*${NUM}`);

// Лист впереди, толщина сзади: «Басвоул Руф 140 1200 х 600 мм 100 мм».
const DIMS_WL_THEN_T = new RegExp(`${NUM}\\s*${X}\\s*${NUM}\\s*мм\\s+${NUM}\\s*мм`);

// Хвост названия: «(0,216 м3; 2,16 м2; 3 шт)».
const TAIL_VOLUME = /\(\s*(\d+(?:[.,]\d+)?)\s*м3/;
const TAIL_AREA = /;\s*(\d+(?:[.,]\d+)?)\s*м2/;
const TAIL_PIECES = /;\s*(\d+(?:[.,]\d+)?)\s*шт\s*\)/;

// Плит толще 300 мм в каталоге нет (максимум — 200 мм), а лист уже 300 мм не
// бывает: минимальная встреченная сторона — 500 мм. Граница разделяет их без
// пересечений, поэтому толщину можно узнать по размеру, а не по месту в строке.
const MAX_THICKNESS_MM = 300;

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const n = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

// Из трёх габаритов толщина — единственный, что помещается в MAX_THICKNESS_MM.
// Если таких несколько, берём наименьший: лист не может быть тоньше плиты.
const pickThickness = (dims) => {
  const candidates = dims.filter((value) => value > 0 && value <= MAX_THICKNESS_MM);
  if (candidates.length === 0) return { thickness: 0, sheet: [] };
  const thickness = Math.min(...candidates);
  const sheet = [];
  let skipped = false;
  dims.forEach((value) => {
    if (!skipped && value === thickness) {
      skipped = true;
      return;
    }
    sheet.push(value);
  });
  return { thickness, sheet };
};

/** Габариты одной плиты (мм) и число плит в упаковке — всё из названия. */
export const parseInsulationDimsFromName = (name) => {
  const text = String(name || "");

  let thickness = 0;
  let widthMm = 0;
  let lengthMm = 0;

  const triple = text.match(DIMS_TRIPLE);
  if (triple) {
    const dims = [toNumber(triple[1]), toNumber(triple[2]), toNumber(triple[3])];
    const picked = pickThickness(dims);
    thickness = picked.thickness;
    [widthMm = 0, lengthMm = 0] = picked.sheet;
  } else {
    // «600х1000 мм 50 мм» проверяем раньше «50 мм 600х1000»: у первого шаблона
    // толщина стоит последней, и обратный порядок разобрал бы лист как толщину.
    const wlThenT = text.match(DIMS_WL_THEN_T);
    if (wlThenT) {
      widthMm = toNumber(wlThenT[1]);
      lengthMm = toNumber(wlThenT[2]);
      thickness = toNumber(wlThenT[3]);
    } else {
      const tThenWl = text.match(DIMS_T_THEN_WL);
      if (tThenWl) {
        thickness = toNumber(tThenWl[1]);
        widthMm = toNumber(tThenWl[2]);
        lengthMm = toNumber(tThenWl[3]);
      }
    }
  }

  const piecesMatch = text.match(TAIL_PIECES);
  const areaMatch = text.match(TAIL_AREA);
  const volumeMatch = text.match(TAIL_VOLUME);

  return {
    thickness,
    widthMm,
    lengthMm,
    pieces: piecesMatch ? toNumber(piecesMatch[1]) : 0,
    area: areaMatch ? toNumber(areaMatch[1]) : 0,
    volume: volumeMatch ? toNumber(volumeMatch[1]) : 0,
  };
};

// Объёмы в названиях округлены до двух-трёх знаков («0,1755» записано как «0,18»),
// поэтому сходство считается относительным, а порог держит это округление.
const AGREEMENT_TOLERANCE = 0.05;

const agree = (a, b) => {
  if (!(a > 0) || !(b > 0)) return false;
  return Math.abs(a - b) / Math.max(a, b) <= AGREEMENT_TOLERANCE;
};

/**
 * Четыре независимые оценки объёма упаковки, м³. Ноль — способ неприменим.
 *
 * Порядок в массиве — это и порядок предпочтения при голосовании: первые два
 * способа считают объём сами, последние два лишь читают готовое число.
 */
export const volumeCandidates = (name, storedVolume) => {
  const dims = parseInsulationDimsFromName(name);
  const thicknessM = dims.thickness > 0 ? dims.thickness / 1000 : 0;

  const byArea = thicknessM > 0 && dims.area > 0 ? thicknessM * dims.area : 0;

  const bySheet = thicknessM > 0 && dims.widthMm > 0 && dims.lengthMm > 0 && dims.pieces > 0
    ? thicknessM * (dims.widthMm / 1000) * (dims.lengthMm / 1000) * dims.pieces
    : 0;

  return [
    { key: "area", value: byArea },
    { key: "sheet", value: bySheet },
    { key: "name", value: dims.volume },
    { key: "stored", value: toNumber(storedVolume) },
  ];
};

/**
 * Объём упаковки утеплителя, м³.
 *
 * @param {string} name — наименование номенклатуры
 * @param {number|string} storedVolume — поле «Объем изделий в м3»
 * @returns {{ volume: number, source: string, isDisputed: boolean }}
 *
 * `source` — «area», «sheet», «name», «stored» или «none»; при согласии двух
 * способов к нему добавляется имя второго («area+sheet»).
 *
 * `isDisputed` поднимается, когда согласия не нашлось и объём пришлось взять
 * первым попавшимся. Цифра при этом остаётся лучшей из доступных, но строку
 * стоит пометить: это расхождение в данных, и менеджер должен его видеть.
 */
export const resolveInsulationVolume = (name, storedVolume) => {
  const candidates = volumeCandidates(name, storedVolume).filter((c) => c.value > 0);

  if (candidates.length === 0) {
    return { volume: 0, source: "none", isDisputed: false };
  }

  // Голосование: первая пара способов, сошедшихся между собой, и даёт ответ.
  // Перебор идёт в порядке предпочтения, поэтому при нескольких согласных парах
  // выигрывает та, где считает сам расчёт, а не переписанное в прайс число.
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      if (agree(candidates[i].value, candidates[j].value)) {
        return {
          volume: candidates[i].value,
          source: `${candidates[i].key}+${candidates[j].key}`,
          isDisputed: false,
        };
      }
    }
  }

  // Согласия нет: способы либо противоречат друг другу, либо доступен один.
  return {
    volume: candidates[0].value,
    source: candidates[0].key,
    isDisputed: candidates.length > 1,
  };
};

export default resolveInsulationVolume;
